import { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin } from 'lucide-react';
import { fetchSuggestions, LocationSuggestion } from '../lib/geocoding';

// Extracted out of PreferencesPage.tsx so BazaarPage's location search can share the same
// postcode-or-town autocomplete instead of a plain text input with no suggestions.
export function PostcodeInput({
  value,
  onChange,
  inputRef,
  placeholder,
  testId,
}: {
  value: string;
  onChange: (v: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  placeholder?: string;
  testId?: string;
}) {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [open, setOpen]               = useState(false);
  const [activeIdx, setActiveIdx]     = useState(-1);
  const wrapRef                       = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
    const timer = setTimeout(async () => {
      const results = await fetchSuggestions(value);
      setSuggestions(results);
      setOpen(results.length > 0);
      setActiveIdx(-1);
    }, 300);
    return () => clearTimeout(timer);
  }, [value]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const select = useCallback((s: LocationSuggestion) => {
    onChange(s.value.toUpperCase());
    setOpen(false);
    setSuggestions([]);
    setActiveIdx(-1);
  }, [onChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); select(suggestions[activeIdx]); }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      <input
        ref={inputRef}
        data-testid={testId}
        value={value}
        onChange={e => onChange(e.target.value.toUpperCase())}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder ?? 'e.g. Leeds, LS1, SW1A 1AA'}
        autoComplete="off"
        spellCheck={false}
        maxLength={30}
        className="form-input pl-9 uppercase tracking-wide"
      />
      {open && (
        <ul className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto py-1">
          {suggestions.map((s, i) => (
            <li
              key={s.display}
              data-testid={testId ? `${testId}-suggestion-${s.value}` : undefined}
              onMouseDown={() => select(s)}
              className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors ${
                i === activeIdx ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="flex-1 truncate">{s.display}</span>
              {s.isPlace && <span className="text-xs text-gray-400 shrink-0">town</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
